import Button from '@/common/components/Button'
import { ErrorMessage } from '@/common/components/ErrorMessage'
import Modal from '@/common/components/Modal'
import { MultipleDocumentUploader } from '@/common/components/MultipleDocumentUploader'
import TextArea from '@/common/components/TextArea'
import useBlobs from '@/common/hooks/queries/useBlobs'
import { FormTransformers } from '@/common/utils/formTransformers'
import { useCloseSupportTicket } from '@/features/support/external/hooks/useCloseSupportTicket'
import { useCreateSupportTicketMessage } from '@/features/support/external/hooks/useCreateSupportTicketMessage'
import {
  externalSupportTicketMessageSchema,
  ExternalSupportTicketMessageValidationSchema,
} from '@/features/support/external/schemas/create.schema'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'

interface FormMessageProps {
  supportTicketId?: string
}

export const FormMessage = ({ supportTicketId }: FormMessageProps) => {
  const queryClient = useQueryClient()

  const [showFormMessage, setShowFormMessage] = useState(false)
  const [baseError, setBaseError] = useState<string[]>([])

  const {
    control,
    register,
    handleSubmit,
    setError,
    reset,
    formState: { errors },
  } = useForm<ExternalSupportTicketMessageValidationSchema>({
    resolver: zodResolver(externalSupportTicketMessageSchema),
  })

  const { mutateAsync: uploadBlob, isPending: uploadBlobIsPending } = useBlobs()

  const { mutate: closeSupportTicket, isPending: closeSupportTicketIsPending } = useCloseSupportTicket({
    supportTicketId,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supportTicket', supportTicketId] })
    },
  })

  const { mutate: createSupportTicket, isPending: createSupportTicketIsPending } = useCreateSupportTicketMessage({
    supportTicketId,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supportTicket', supportTicketId] })
      queryClient.invalidateQueries({ queryKey: ['supportTicketMessages'] })
      closeForm()
    },
    onError: (error) => {
      if (error?.response?.data?.errors?.base?.length) {
        setBaseError(error.response.data.errors.base)
        return
      }

      FormTransformers.errorsTransformer<ExternalSupportTicketMessageValidationSchema>({
        setError,
      })(error)
    },
  })

  const onSubmit: SubmitHandler<ExternalSupportTicketMessageValidationSchema> = (data) => {
    createSupportTicket(data)
  }

  const closeForm = useCallback(() => {
    setShowFormMessage(false)
    reset()
  }, [])

  return (
    <>
      {showFormMessage ? (
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
          {baseError?.map((error) => (
            <p className="text-sm text-danger-soft">{error}</p>
          ))}

          <TextArea rows={16} maxLength={2048} errorMessage={errors.content?.message} {...register('content')} />

          <div>
            <MultipleDocumentUploader
              control={control}
              name="documents"
              uploadBlob={uploadBlob}
              fieldName="documents"
              accept=".png, .jpg, .jpeg, .webp, .pdf, .doc, .docx, .xls, .xlsx"
              setError={(message) => setError?.('documents', { message })}
              disabled={createSupportTicketIsPending || uploadBlobIsPending}
            />
            {errors.documents?.message && <ErrorMessage message={errors.documents?.message} />}
          </div>

          <div className="flex gap-2 self-end">
            <Button type="button" variant="outline-error" onClick={closeForm}>
              Cancelar
            </Button>

            <Button
              name="submit"
              type="submit"
              disabled={createSupportTicketIsPending || uploadBlobIsPending}
              isLoading={createSupportTicketIsPending}
            >
              Enviar
            </Button>
          </div>
        </form>
      ) : (
        <div className="flex gap-2 self-end">
          <Modal
            Trigger={
              <Button
                variant="outline-success"
                name="close_ticket"
                operationKlassToCheckPermission="Admin::Support::Tickets::Close"
              >
                Marcar como concluído
              </Button>
            }
            showClose={false}
            title="Você tem certeza?"
            subtitle="Se marcar o chamado como concluído não poderá mais interagir com ele."
            titleClass="text-2xl"
            subtitleClassName="mt-2 text-white-400"
            useCancellableFooter
            onConfirm={() => closeSupportTicket()}
            confirmButtonIsLoading={closeSupportTicketIsPending}
            disabled={closeSupportTicketIsPending}
          />

          <Button
            onClick={() => setShowFormMessage(true)}
            name="reply_ticket"
            operationKlassToCheckPermission="Admin::Support::Tickets::CreateMessage"
          >
            Responder
          </Button>
        </div>
      )}
    </>
  )
}
